/**
 * A Workflow Builder text field whose variables (`{{trigger.user}}`) show as chips with readable names, plus Slack's
 * "Insert a variable" menu (the variables this step can use). Chips are the composer's mention characters in
 * RichTextField; core WorkflowBuilder maps edits in that text back onto the template.
 */
import type { WorkflowDefinition } from "@bundle/contracts";
import { Workflows, WorkflowBuilder, type VariableChip, type WorkflowLabel } from "@bundle/core";
import { useRef, useState } from "react";
import { Button, Menu, RichTextField, Stack, Text, TextField, type MenuEntry, type RichRun } from "../../primitives/index.ts";
import { useT, useTr } from "../../i18n/I18nProvider.tsx";

/** A label's text: a message to translate, or the author's own words. */
export function useLabel(): (l: WorkflowLabel) => string {
  const tr = useTr();
  return (l) => (typeof l === "string" ? l : tr(l));
}

/** A variable chip's text ("Person who used this workflow", "2. Form › Reason"). */
export function useChipLabel(): (c: VariableChip) => string {
  const label = useLabel();
  return (c) => (c.step === undefined ? label(c.label) : `${c.step.n}. ${label(c.step.title)} › ${label(c.label)}`);
}

/** The variables a step can use, as menu items. */
export function useVariableItems(def: Pick<WorkflowDefinition, "trigger" | "steps">, stepId: string | undefined, onPick: (ref: string) => void, types?: readonly string[]): MenuEntry[] {
  const chipLabel = useChipLabel();
  return Workflows.availableVariables(def, stepId)
    .filter((v) => types === undefined || types.includes(v.type))
    .map((v) => ({ id: v.ref, label: chipLabel(WorkflowBuilder.variableChip(def, v.ref, stepId)), onSelect: () => onPick(v.ref) }));
}

export type TemplateFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  def: Pick<WorkflowDefinition, "trigger" | "steps">;
  /** The step the field belongs to (its variables are the ones before it); absent = after the last step. */
  stepId?: string;
  placeholder?: string;
  /** A danger note under the field. */
  error?: string;
  testID: string;
};

export function TemplateField(p: TemplateFieldProps) {
  const t = useT();
  const chipLabel = useChipLabel();
  const selection = useRef({ start: -1, end: -1 });
  const [focus, setFocus] = useState<{ offset: number; seq: number } | undefined>(undefined);
  const { text, refs } = WorkflowBuilder.chipText(p.value);
  const runs: RichRun[] = [];
  let at = 0;
  let k = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] !== WorkflowBuilder.CHIP) continue;
    if (i > at) runs.push({ text: text.slice(at, i) });
    const chip = WorkflowBuilder.variableChip(p.def, refs[k]!, p.stepId);
    k += 1;
    runs.push({ text: WorkflowBuilder.CHIP, mention: { kind: "channel", label: chipLabel(chip), chip: chip.known ? "user" : "broadcast", copyText: `{{${chip.ref}}}` } });
    at = i + 1;
  }
  if (at < text.length || runs.length === 0) runs.push({ text: text.slice(at) });
  const insert = (ref: string) => {
    const s = selection.current.start < 0 ? { start: text.length, end: text.length } : selection.current;
    const next = WorkflowBuilder.insertVariable(p.value, s.start, s.end, ref);
    p.onChange(next.template);
    selection.current = { start: next.caret, end: next.caret };
    setFocus((f) => ({ offset: next.caret, seq: (f?.seq ?? 0) + 1 }));
  };
  const items = useVariableItems(p.def, p.stepId, insert);
  return (
    <Stack gap="1" testID={p.testID}>
      <Stack direction="horizontal" align="center" justify="between" gap="2">
        <Text variant="footnote" weight="medium" color="text.secondary">
          {p.label}
        </Text>
        {items.length === 0 ? null : (
          <Menu items={items} align="end" testID={`${p.testID}-variables`}>
            <Button label={t("workflow.variable.insert")} icon="plus" variant="ghost" size="sm" onPress={() => undefined} testID={`${p.testID}-insert`} />
          </Menu>
        )}
      </Stack>
      <Stack paddingX="3" paddingY="2" radius="md" border={p.error === undefined ? "border.strong" : "border.error"} background="bg.surface">
        <RichTextField
          runs={runs}
          onChange={(start, end, inserted) => p.onChange(WorkflowBuilder.editTemplate(p.value, start, end, inserted))}
          onSelect={(start, end) => {
            selection.current = { start, end };
          }}
          onPaste={(pasted, start, end) => {
            p.onChange(WorkflowBuilder.editTemplate(p.value, start, end, pasted.replace(/\r\n?/g, "\n")));
            return true;
          }}
          focus={focus}
          placeholder={p.placeholder}
          accessibilityLabel={p.label}
          fallback={<TextField variant="plain" multiline value={p.value} onChange={p.onChange} placeholder={p.placeholder} accessibilityLabel={p.label} testID={`${p.testID}-input`} />}
          testID={`${p.testID}-input`}
        />
      </Stack>
      {p.error === undefined ? null : (
        <Text variant="caption" color="status.danger">
          {p.error}
        </Text>
      )}
    </Stack>
  );
}
